import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Box, Typography } from "@mui/material";
import { Search, PeopleAltRounded, PeopleOutlined } from "@mui/icons-material";
import { colors } from "../../theme/colors";
import { motion, AnimatePresence } from "framer-motion";
import ProfileFriendRow from "./ProfileFriendRow";
import SearchEmptyState from "../common/SearchEmptyState";
import { EmptyDataState } from "../common/EmptyDataState";

const MotionBox = motion(Box);

const FRIEND_FILTER = {
    ALL: "all",
    MUTUAL: "mutual",
};

function FriendFilterButton({
    label,
    count,
    isActive,
    onClick,
}) {
    return (
        <MotionBox
            component="button"
            type="button"
            onClick={onClick}
            aria-pressed={isActive}
            whileHover={{
                y: -1,
            }}
            whileTap={{
                scale: 0.97,
            }}
            transition={{
                type: "spring",
                stiffness: 320,
                damping: 24,
            }}
            sx={{
                position: "relative",
                overflow: "hidden",
                border: "none",
                cursor: "pointer",
                borderRadius: "999px",
                px: "1rem",
                py: "0.58rem",
                minWidth: "84px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "0.35rem",
                fontSize: "0.74rem",
                fontWeight: 700,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                color: isActive
                    ? colors.text.primary
                    : "rgba(255,255,255,0.46)",
                background: isActive
                    ? `
                        linear-gradient(
                            180deg,
                            rgba(255,255,255,0.06) 0%,
                            rgba(246,185,76,0.10) 100%
                        )
                    `
                    : "rgba(255,255,255,0.015)",
                borderWidth: "1px",
                borderStyle: "solid",
                borderColor: isActive
                    ? "rgba(246,185,76,0.24)"
                    : "rgba(255,255,255,0.06)",
                backdropFilter: "blur(10px)",
                boxShadow: isActive
                    ? `
                        0 0 0 1px rgba(246,185,76,0.04),
                        0 6px 18px rgba(0,0,0,0.24),
                        0 0 24px rgba(246,185,76,0.08)
                    `
                    : `
                        inset 0 1px 0 rgba(255,255,255,0.02)
                    `,
                transition: `
                    background 240ms ease,
                    color 200ms ease,
                    border-color 220ms ease,
                    box-shadow 260ms ease
                `,
                "&:hover": {
                    color: colors.text.primary,
                    borderColor: isActive
                        ? "rgba(246,185,76,0.30)"
                        : "rgba(255,255,255,0.12)",
                    background: isActive
                        ? `
                            linear-gradient(
                                180deg,
                                rgba(255,255,255,0.08) 0%,
                                rgba(246,185,76,0.14) 100%
                            )
                        `
                        : "rgba(255,255,255,0.03)",
                },
                "&::before": {
                    content: '""',
                    position: "absolute",
                    inset: 0,
                    opacity: isActive ? 1 : 0,
                    background: `
                        linear-gradient(
                            120deg,
                            transparent 20%,
                            rgba(255,255,255,0.06) 50%,
                            transparent 80%
                        )
                    `,
                    transition: "opacity 220ms ease",
                    pointerEvents: "none",
                },
            }}
        >
            {/* Active glow pill */}
            <AnimatePresence mode="wait">
                {isActive && (
                    <MotionBox
                        layoutId="activeFriendFilter"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{
                            type: "spring",
                            stiffness: 280,
                            damping: 26,
                        }}
                        sx={{
                            position: "absolute",
                            inset: 0,
                            borderRadius: "999px",
                            border:
                                "1px solid rgba(246,185,76,0.14)",
                            boxShadow:
                                "0 0 22px rgba(246,185,76,0.10)",
                            pointerEvents: "none",
                        }}
                    />
                )}
            </AnimatePresence>

            <MotionBox
                layout
                transition={{
                    type: "spring",
                    stiffness: 380,
                    damping: 30,
                }}
                sx={{
                    position: "relative",
                    zIndex: 2,
                    display: "flex",
                    alignItems: "center",
                    gap: "0.35rem",
                }}
            >
                <span>{label}</span>

                <MotionBox
                    key={count}
                    initial={{
                        opacity: 0.7,
                        y: 4,
                    }}
                    animate={{
                        opacity: 1,
                        y: 0,
                    }}
                    transition={{
                        duration: 0.18,
                    }}
                    sx={{
                        color: isActive
                            ? "rgba(246,185,76,0.95)"
                            : "rgba(255,255,255,0.5)",
                    }}
                >
                    ({count})
                </MotionBox>
            </MotionBox>
        </MotionBox>
    );
}

export default function ProfileFriendsTab({
    friends = [],
    viewerFriends = [],
    isOwnProfile = false,
    profileNickname = "",
    viewerUser,
}) {
    const [activeFilter, setActiveFilter] = useState(FRIEND_FILTER.ALL);
    const [friendSearchQuery, setFriendSearchQuery] = useState("");
    const [searchFocused, setSearchFocused] = useState(false);

    const allFriendsCount = friends.length;

    const mutualFriendsCount = useMemo(() => {
        if (isOwnProfile) return 0;

        const viewerSteamIds = new Set(
            viewerFriends
                .map((friend) => friend.friendSteamId)
                .filter(Boolean)
        );

        return friends.filter((friend) =>
            viewerSteamIds.has(friend.friendSteamId)
        ).length;
    }, [friends, viewerFriends, isOwnProfile]);

    const visibleFriends = useMemo(() => {
        const filteredByTab = (() => {
            if (activeFilter !== FRIEND_FILTER.MUTUAL) {
                return friends;
            }

            const viewerSteamIds = new Set(
                viewerFriends
                    .map((friend) => friend.friendSteamId)
                    .filter(Boolean)
            );

            return friends.filter((friend) =>
                viewerSteamIds.has(friend.friendSteamId)
            );
        })();

        const query = friendSearchQuery.trim().toLowerCase();
        if (!query) {
            return filteredByTab;
        }

        return filteredByTab.filter((friend) => {
            const nickname = friend.friendData?.nickname || friend.friendData?.name || "";
            return nickname.toLowerCase().includes(query);
        });
    }, [activeFilter, friendSearchQuery, friends, viewerFriends]);

    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                borderRadius: "12px",
                position: "relative",
                background: `
                    linear-gradient(
                        180deg,
                        rgba(12,12,14,0.96) 0%,
                        rgba(8,8,10,0.985) 100%
                    )
                `,
                border: "1px solid rgba(255,255,255,0.05)",
                backdropFilter: "blur(18px)",
                boxShadow: `
                    inset 0 1px 0 rgba(255,255,255,0.05),
                    inset 0 -40px 80px rgba(246,185,76,0.03),
                    0 24px 60px rgba(0,0,0,0.42)
                `,
                gap: "1rem",
                padding: "1.5rem",
                minWidth: 0,
            }}
        >
            <Box
                sx={{
                    position: "absolute",
                    top: 0,
                    left: "50%",
                    transform: "translateX(-50%)",
                    width: "42%",
                    height: "1px",
                    background: `
                        linear-gradient(
                            90deg,
                            transparent,
                            rgba(246,185,76,0.95),
                            transparent
                        )
                    `,
                    boxShadow: "0 0 18px rgba(246,185,76,0.45)",
                    pointerEvents: "none",
                }}
            />
            <Typography
                sx={{
                    fontSize: "0.8rem",
                    fontWeight: 800,
                    letterSpacing: "0.16em",
                    textTransform: "uppercase",
                    color: colors.text.primary,
                    lineHeight: 1,
                    mb: "0.35rem",
                }}
            >
                Friends
            </Typography>
            <Box
                sx={{
                    display: "flex",
                    flexDirection: { xs: "column", sm: "row" },
                    flexWrap: { sm: "wrap" },
                    alignItems: { xs: "stretch", sm: "center" },
                    gap: "0.5rem",
                }}
            >
                <Box
                    sx={{
                        order: { xs: 2, sm: 0 },
                        display: "flex",
                        flexWrap: "wrap",
                        alignItems: "center",
                        gap: "0.5rem",
                        minWidth: 0,
                    }}
                >
                    <FriendFilterButton
                        label="All friends"
                        count={allFriendsCount}
                        isActive={activeFilter === FRIEND_FILTER.ALL}
                        onClick={() => setActiveFilter(FRIEND_FILTER.ALL)}
                    />
                    {!isOwnProfile ? (
                        <FriendFilterButton
                            label="Mutual friends"
                            count={mutualFriendsCount}
                            isActive={activeFilter === FRIEND_FILTER.MUTUAL}
                            onClick={() => setActiveFilter(FRIEND_FILTER.MUTUAL)}
                        />
                    ) : null}
                </Box>
                {friends.length > 0 ? (
                    <Box
                        sx={{
                            order: { xs: 1, sm: 0 },
                            width: { xs: "100%", sm: "20rem", xl: "24rem" },
                            ml: { xs: 0, sm: "auto" },
                        }}
                    >
                        <Box
                            sx={{
                                width: "100%",
                                minWidth: 0,
                                display: "flex",
                                alignItems: "center",
                                gap: "0.75rem",
                                background: colors.background.surface,
                                border: `1px solid ${searchFocused ? "rgba(232, 163, 61, 0.55)" : colors.border.default}`,
                                borderRadius: "14px",
                                padding: { xs: "0.7rem 0.95rem", sm: "0.9rem 1.1rem" },
                                boxShadow: searchFocused
                                    ? "0 0 0 3px rgba(232, 163, 61, 0.10)"
                                    : "none",
                                transition: "border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out",
                            }}
                        >
                            <Search
                                sx={{
                                    flexShrink: 0,
                                    color: searchFocused
                                        ? colors.primary.main
                                        : colors.text.secondary,
                                    fontSize: "1.15rem",
                                    transition: "color 180ms ease",
                                }}
                            />
                            <Box
                                component="input"
                                id="profile-friend-search-input"
                                type="text"
                                placeholder="Search friends..."
                                value={friendSearchQuery}
                                onChange={(event) => setFriendSearchQuery(event.target.value)}
                                onFocus={() => setSearchFocused(true)}
                                onBlur={() => setSearchFocused(false)}
                                sx={{
                                    flex: 1,
                                    minWidth: 0,
                                    background: "none",
                                    border: "none",
                                    outline: "none",
                                    color: colors.text.primary,
                                    fontFamily: "inherit",
                                    fontSize: { xs: "0.88rem", sm: "0.95rem" },
                                    "&::placeholder": {
                                        color: colors.text.muted,
                                    },
                                }}
                            />
                        </Box>
                    </Box>
                ) : null}
            </Box>
            {friends.length === 0 ? (
                <EmptyDataState
                    title="No friends yet"
                    icon={PeopleAltRounded}
                    minHeight="12rem"
                    sx={{ borderRadius: "12px" }}
                >
                    Friends you add will appear here
                </EmptyDataState>
            ) : friends.length > 0 && visibleFriends.length === 0 ? (
                friendSearchQuery.trim() ? (
                    <Box sx={{ py: "1.5rem" }}>
                        <SearchEmptyState
                            title="No friends found"
                            description="Try another nickname or clear the search to see the full friends list."
                            searchQuery={friendSearchQuery}
                            onClearSearch={() => setFriendSearchQuery("")}
                        />
                    </Box>
                ) : (
                    <EmptyDataState
                        embedded
                        title="No mutual friends"
                        icon={PeopleOutlined}
                        minHeight="9rem"
                    >
                        {profileNickname.trim()
                            ? `You and ${profileNickname.trim()} don’t share any friends yet.`
                            : "You don’t share any friends with this player yet."}
                    </EmptyDataState>
                )
            ) : (
                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns: {
                            xs: "minmax(0, 1fr)",
                            md: "minmax(0, 1fr) minmax(0, 1fr)",
                        },
                        gap: "0.75rem",
                        mt: "0.25rem",
                        width: "100%",
                        minWidth: 0,
                    }}
                >
                    {visibleFriends.map((friend) => (
                        <ProfileFriendRow
                            key={
                                friend.friendSteamId ||
                                friend.friendData?.userId ||
                                friend.friendData?._id
                            }
                            friend={friend}
                            viewerUser={viewerUser}
                        />
                    ))}
                </Box>
            )}
        </Box>
    );
}

ProfileFriendsTab.propTypes = {
    friends: PropTypes.arrayOf(PropTypes.object),
    viewerFriends: PropTypes.arrayOf(PropTypes.object),
    isOwnProfile: PropTypes.bool,
    profileNickname: PropTypes.string,
    viewerUser: PropTypes.object,
    pendingRequests: PropTypes.arrayOf(PropTypes.object),
    onAddFriend: PropTypes.func,
    onRemoveFriend: PropTypes.func,
};
